import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import SignaturePad from '@/components/ui/SignaturePad';
import { Shield, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { message } from 'antd';
import Spinner from '@/components/Spinner';
import { PackageAPI } from '@/api/type';
import { getPackageDetail, registrationPackage } from '@/api/package';
import { fetchUserData } from '@/api/User';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getDefaultUserBankAccount } from '@/api/Bank';
import { Content13W, Content13WAppendix, ContentLumirAppendix, ContentTitanContract, Header13W, Header13WAppendix, HeaderLumirAppendix, HeaderTitanContract } from '@/components/Contracts';
import { decodeSigningContractData, getDemoSigningContractUrl } from '@/lib/signingContractPayload';
dayjs.extend(customParseFormat);

const dateFormat = 'YYYY-MM-DD';
/** Manually entering any of the following formats will perform date parsing */
const dateFormatList = ['YYYY-MM-DD'];

const contracts = [
  {
    name: 'TITAN',
    mainHeader: HeaderTitanContract,
    mainContent: ContentTitanContract,
    appendixHeader: HeaderLumirAppendix,
    appendixContent: ContentLumirAppendix,
    contract_name: 'BEQ HOLDINGS - TITAN',
  },
  {
    name: 'PIONEER',
    mainHeader: HeaderTitanContract,
    mainContent: ContentTitanContract,
    appendixHeader: HeaderLumirAppendix,
    appendixContent: ContentLumirAppendix,
    contract_name: 'BEQ HOLDINGS - PIONEER',
  },
  {
    name: 'TRADER',
    mainHeader: Header13W,
    mainContent: Content13W,
    appendixHeader: Header13WAppendix,
    appendixContent: Content13WAppendix,
    contract_name: 'LUMIR BEQ HOLDINGS',
  }
]

interface ContractData {
  contractNumber: string;
  signDate: string;
  depositDate: string;
  finalPaymentDate: string;
  investor: {
    fullName: string;
    idNumber: string;
    dob: string;
    issueDate: string;
    issuePlace: string;
    email: string;
    phone: string;
    address: string;
    bankAccount: string;
    bank: string;
  };
  company: {
    name: string;
    address: string;
    taxCode: string;
    bankAccount: string;
    bank: string;
    representative: string;
    position: string;
    representativeId: string;
    representativeIssueDate: string;
  };
}
// Initial contract data
const initialContractData = {
  contractNumber: '',
  signDate: '',
  depositDate: '',
  finalPaymentDate: '',
  investor: {
    fullName: '',
    idNumber: '',
    dob: '',
    issueDate: '',
    issuePlace: '',
    email: '',
    phone: '',
    address: '',
    bankAccount: '',
    bank: ''
  },
  company: {
    name: 'CÔNG TY CỔ PHẦN BEQ HOLDINGS',
    address: 'Số 67 Đinh Thị Thi, P. Hiệp Bình, TP.HCM, Việt Nam',
    taxCode: '0317746326',
    bankAccount: '1036616688',
    bank: 'Ngân hàng TMCP Ngoại thương Việt Nam (Vietcombank) - Chi nhánh: Thủ Đức',
    representative: 'Bà Tạ Thị Mỹ Phương',
    position: 'Tổng Giám đốc',
    representativeId: '079182006638',
    representativeIssueDate: '21/02/2021'
  }
};

const SigningContract: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resolvedPackageId, setResolvedPackageId] = useState<number | null>(null);
  const [decodedContractInfo, setDecodedContractInfo] = useState<{ name?: string; price?: number } | null>(null);
  const isContractNumberFromData = useRef(false);
  const [contractData, setContractData] = useState<ContractData>({
    contractNumber: '',
    signDate: new Date().toLocaleDateString('vi-VN'),
    depositDate: dayjs().format('YYYY-MM-DD'),
    finalPaymentDate: dayjs().format('YYYY-MM-DD'),
    investor: { ...initialContractData.investor },
    company: { ...initialContractData.company }
  });

  const [signature, setSignature] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractInfo, setContractInfo] = useState<PackageAPI | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [contractHash, setContractHash] = useState<string>('');
  const contractRef = useRef<HTMLDivElement>(null);
  const appendixRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const encodedData = searchParams.get('data');
    getDemoSigningContractUrl().then(url => console.log('Demo signing contract URL:', url));
    if (!encodedData) {
      message.error('Thiếu dữ liệu hợp đồng trong đường dẫn.');
      return;
    }

    const decodePayload = async () => {
      try {
        const payload = await decodeSigningContractData(decodeURIComponent(encodedData));
        setResolvedPackageId(Number(payload.packageId));

        if (payload.contractInfo?.name || payload.contractInfo?.price) {
          setDecodedContractInfo(payload.contractInfo);
        }

        if (payload.contractData) {
          if (payload.contractData.contractNumber) {
            isContractNumberFromData.current = true;
          }
          setContractData(prev => ({
            ...prev,
            contractNumber: payload.contractData?.contractNumber || prev.contractNumber,
            signDate: payload.contractData?.signDate || prev.signDate,
            depositDate: payload.contractData?.depositDate || prev.depositDate,
            finalPaymentDate: payload.contractData?.finalPaymentDate || prev.finalPaymentDate,
            investor: {
              ...prev.investor,
              ...(payload.contractData?.investor || {}),
            },
          }));
        }
      } catch (error) {
        console.error('Error decoding signing contract payload:', error);
        message.error('Dữ liệu hợp đồng không hợp lệ, thiếu chữ ký hoặc đã bị chỉnh sửa.');
      }
    };

    decodePayload();
  }, [searchParams]);

  const generateContractHash = async (ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current || !window.crypto?.subtle) return '';
    const content = ref.current.innerText;
    const msgUint8 = new TextEncoder().encode(content);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const handleSignatureChange = (sig: string | null) => {
    setSignature(sig);
    if (sig) {
      setShowPreview(true);
    } else {
      setShowPreview(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContractData(prev => ({
      ...prev,
      investor: {
        ...prev.investor,
        [name]: value
      }
    }));
  };

  const generatePDFFromRef = async (ref: React.RefObject<HTMLDivElement>, fileName: string, shouldDownload: boolean = false) => {
    if (!ref.current) return null;

    const hashHex = await generateContractHash(ref);
    const pdf = new jsPDF('p', 'mm', 'a4');

    pdf.setProperties({
      subject: "SHA256:" + hashHex,
      author: "BEQ Holdings",
    });

    const element = ref.current;
    const blocks = element.querySelectorAll<HTMLElement>('.pdf-block');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pdfWidth - (margin * 2);

    let currentY = margin;

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const isLogo = block.classList.contains('pdf-logo');

      const canvas = await html2canvas(block, {
        scale: isLogo ? 1 : 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false
      });

      const quality = isLogo ? 0.5 : 0.85;
      const imgData = canvas.toDataURL('image/jpeg', quality);
      const imgProps = pdf.getImageProperties(imgData);
      // Prevent division by zero if width is 0 implies empty block
      const blockHeightInPdf = imgProps.width > 0 ? (imgProps.height * contentWidth) / imgProps.width : 0;

      if (blockHeightInPdf > 0) {
        if (currentY + blockHeightInPdf > pdfHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }

        pdf.addImage(imgData, 'JPEG', margin, currentY, contentWidth, blockHeightInPdf, undefined, isLogo ? 'SLOW' : 'FAST');
        currentY += blockHeightInPdf + (isLogo ? 2 : 5);
      }
    }

    if (shouldDownload) {
      pdf.save(fileName);
    }

    const pdfBlob = pdf.output('blob');
    return new File([pdfBlob], fileName, { type: 'application/pdf' });
  };

  const exportToPDF = async (shouldDownload: boolean | any = false) => {
    setIsExporting(true);
    const isDownload = typeof shouldDownload === 'boolean' ? shouldDownload : false;

    try {
      const files: File[] = [];

      // Export Main Contract
      if (contractRef.current) {
        const mainFile = await generatePDFFromRef(contractRef, `HopDong_${contractData.contractNumber}.pdf`, isDownload);
        if (mainFile) files.push(mainFile);
      }

      // Export Appendix if it exists
      // Check if current contract has appendix
      if (appendixRef.current && appendixRef.current.children.length > 0) {
        const appendixFile = await generatePDFFromRef(appendixRef, `PhuLuc_${contractData.contractNumber}.pdf`, isDownload);
        if (appendixFile) files.push(appendixFile);
      }

      return files;

    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Lỗi xuất PDF. Vui lòng thử lại.');
      return [];
    } finally {
      setIsExporting(false);
    }
  }; const handleSubmit = async () => {
    if (!signature) {
      // using sonnor in next update
      alert('Vui lòng vẽ chữ ký của bạn');
      return;
    }

    setIsSubmitting(true);

    try {
      const hash = await generateContractHash(contractRef);
      setContractHash(hash);

      const files = await exportToPDF();
      if (files && files.length > 0) {
        const contractFile = files[0];
        const appendixFile = files.length > 1 ? files[1] : undefined;

        if (!resolvedPackageId) {
          message.error('Không tìm thấy gói đầu tư trong dữ liệu hợp đồng.');
          setIsSubmitting(false);
          return;
        }

        registrationPackage(resolvedPackageId, contractFile, contractData.contractNumber, { full_name: contractData.investor.fullName, id: contractData.investor.idNumber }, appendixFile, hash).then((response) => {
          if (response.status === false) {
            message.error(response.message);
            setIsSubmitting(false);
            return;
          }
          message.success(response.message);
          // Navigate to checkout page with the generated PDF file
          navigate(`/plans/${resolvedPackageId}/checkout?contractId=${response.data?.id}`, { state: { signature, contractData, contractFile, appendixFile, responseContract: response, contractHash: hash } });
          setIsSubmitting(false);
        }).catch(err => {
          console.error('Error registering package:', err);
          message.error('Đăng ký gói thất bại. Vui lòng thử lại.');
          setIsSubmitting(false);
        });
      } else {
        message.error('Không thể tạo file hợp đồng. Vui lòng thử lại.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      message.error('Có lỗi xảy ra. Vui lòng thử lại.');
      setIsSubmitting(false);
    }
  };

  const checkContractReady = () => {
    return signature !== null && contractData.investor.fullName !== '' && contractData.investor.idNumber !== '' && contractData.investor.dob !== '' && contractData.investor.address !== '' && contractData.investor.email !== '' && contractData.investor.phone !== '' && contractData.investor.bankAccount !== '' && contractData.investor.bank !== '';
  };

  useEffect(() => {
    if (!resolvedPackageId) return;

    getPackageDetail(resolvedPackageId).then(data => {
      setContractInfo(data);
    }).catch(err => {
      console.error('Error fetching package detail:', err);
    });
  }, [resolvedPackageId]);

  useEffect(() => {
    // Re-generate hash whenever contract data or signature changes
    generateContractHash(contractRef).then(hash => setContractHash(hash));
  }, [contractData, signature]);

  // Helper function to generate contract code
  const updateContractCode = () => {
    if (isContractNumberFromData.current) return;
    const effectiveContractName = contractInfo?.name || decodedContractInfo?.name;
    if (!effectiveContractName || !contractData.investor.fullName) return;

    const dateStr = contractData.signDate;
    const fullName = contractData.investor.fullName;
    const contractName = contracts.find(c => c.name === effectiveContractName)?.contract_name || effectiveContractName;

    // 1. Random Gen (Date + InvestorName) - Deterministic hash for stability
    const generateRandomPart = (d: string, n: string) => {
      const dFormatted = dayjs(d, 'DD/MM/YYYY').isValid() ? dayjs(d, 'DD/MM/YYYY').format('YYYYMMDD') : dayjs().format('YYYYMMDD');
      let hash = 0;
      const normalizedName = n.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
      for (let i = 0; i < normalizedName.length; i++) {
        hash = ((hash << 5) - hash) + normalizedName.charCodeAt(i);
        hash |= 0;
      }
      const positiveHash = Math.abs(hash).toString().slice(0, 5).padEnd(5, '0');
      return `${dFormatted}${positiveHash}`;
    };

    // 2. Initials
    const getInitials = (name: string) => {
      return name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .split(' ')
        .filter(n => n.length > 0)
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();
    };

    // randomGen(date,invertername) - contract_name - each first lerter of inverter name
    const randomPart = generateRandomPart(dateStr, fullName);
    const initials = getInitials(fullName);
    const newContractNumber = `${randomPart}-${contractName}-${initials}`;

    setContractData(prev => {
      if (prev.contractNumber !== newContractNumber) {
        return { ...prev, contractNumber: newContractNumber };
      }
      return prev;
    });
  };

  useEffect(() => {
    updateContractCode();
  }, [contractInfo, decodedContractInfo, contractData.investor.fullName, contractData.signDate]);

  // const exportToPDF = async () => {
  //   if (!contractRef.current) return;

  //   setIsExporting(true);
  //   try {
  //     const canvas = await html2canvas(contractRef.current, {
  //       scale: 2,
  //       useCORS: true,
  //       logging: false,
  //     });

  //     const imgData = canvas.toDataURL('image/png');
  //     const pdf = new jsPDF('p', 'mm', 'a4');
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

  //     pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
  //     pdf.save(`HopDong_${contractData.contractNumber}.pdf`);
  //   } catch (error) {
  //     console.error('Error exporting PDF:', error);
  //     alert('Có lỗi xảy ra khi xuất PDF');
  //   } finally {
  //     setIsExporting(false);
  //   }
  // };

  useEffect(() => {
    const sessionInvestorInfo = sessionStorage.getItem('investorInfo');
    getDefaultUserBankAccount().then(bankAccount => {
      if (bankAccount) {
        setContractData(prev => ({
          ...prev,
          investor: {
            ...prev.investor,
            bank: prev.investor.bank || bankAccount.bank_name,
            bankAccount: prev.investor.bankAccount || bankAccount.account_number
          }
        }));
      }
    }).catch(err => {
      console.error('Error fetching default bank account:', err);
    });

    if (sessionInvestorInfo) {
      try {
        const parsedInvestor = JSON.parse(sessionInvestorInfo);
        setContractData(prev => ({
          ...prev,
          investor: {
            ...prev.investor,
            ...parsedInvestor,
          }
        }));
      } catch (error) {
        console.error('Error parsing session investor info:', error);
      }
    }

    fetchUserData().then(userData => {
      setContractData(prev => ({
        ...prev,
        investor: {
          ...prev.investor,
          fullName: prev.investor.fullName || userData.fullname || '',
          email: prev.investor.email || userData.email || '',
          phone: prev.investor.phone || userData.phone || '',
          address: prev.investor.address || userData.address || '',
          dob: prev.investor.dob || userData.birthday || '',
          issueDate: prev.investor.issueDate || userData?.cccd_issued_date || '',
          issuePlace: prev.investor.issuePlace || userData?.cccd_issued_place || '',
          idNumber: prev.investor.idNumber || userData?.cccd_number || '',
        }
      }));
    }).catch(err => {
      console.error('Error fetching user data:', err);
    });
  }, []);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', path: '/home' },
    { label: 'Gói đầu tư', path: '/plans' },
    { label: 'Ký hợp đồng' },
  ];

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>Ký hợp đồng - BeQs Reward</title>
      </Helmet>

      <PageHeader title="Ký hợp đồng điện tử" breadcrumbs={breadcrumbs} />

      <div className="!mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 !gap-6">
          <div className="lg:col-span-2">
            <div className="!bg-white dark:!bg-dark-card !rounded-xl !shadow-sm !p-8 !relative" ref={contractRef}>
              {/* <div className="!absolute !top-12 !left-6 !w-32 pdf-block">
                <img src="/images/logo/logoweb.webp" alt="BeQ Holdings Logo" className="w-16 h-16" />
              </div> */}
              {/* Khối Header: Chứa Logo và Tiêu đề Quốc hiệu */}
              {
                contracts.filter(c => c.name === (contractInfo?.name || decodedContractInfo?.name)).map((contract, index) => {
                  const MainHeader = contract.mainHeader;
                  return <MainHeader key={index} contractNumber={contractData.contractNumber} />;
                })
              }

              <div className="!mb-6 pdf-block">
                <p className="!text-sm !text-gray-700 dark:!text-gray-300 !mb-4">
                  Hôm nay, ngày {contractData.signDate} tại Công ty Cổ phần BeQ Holdings, chúng tôi gồm có:
                </p>

                <div className="!space-y-4">
                  <div>
                    <h4 className="!font-semibold !text-gray-900 dark:!text-white !mb-2">Bên A: {contractData.company.name}</h4>
                    <div className="!text-sm !text-gray-900 dark:!text-gray-400 !space-y-1">
                      <p>Địa chỉ: {contractData.company.address}</p>
                      <p>MST: {contractData.company.taxCode}</p>
                      <p>Số tài khoản: {contractData.company.bankAccount} - {contractData.company.bank}</p>
                      <p>Đại diện: {contractData.company.representative} – {contractData.company.position}</p>
                      <p>(CCCD: {contractData.company.representativeId}, cấp ngày {contractData.company.representativeIssueDate})</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="!font-semibold !text-gray-900 dark:!text-white !mb-2 flex flex-row relative">
                      <div className="inline-block">
                        Bên B: {contractData.investor.fullName || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}
                      </div>
                    </h4>
                    <div className="!text-sm !text-gray-900 dark:!text-gray-400 !space-y-1 w-1/2">
                      <p>CCCD: {contractData.investor.idNumber + ' - Ngày cấp: ' + contractData.investor.issueDate || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                      <p>Ngày sinh: {contractData.investor.dob || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                      <p>Địa chỉ: {contractData.investor.address || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                      <p>Email: {contractData.investor.email || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                      <p>SĐT: {contractData.investor.phone || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                      <p>Số tài khoản: {contractData.investor.bankAccount || <span className="inline-block border-b border-gray-900 w-1/4 h-px ml-1 translate-y-[-2px]"></span>} - {contractData.investor.bank || <span className="inline-block border-b border-gray-900 w-1/4 h-px ml-1 translate-y-[-2px]"></span>}</p>
                    </div>
                  </div>

                  <p className="!text-sm !text-gray-900 dark:!text-gray-400 italic">
                    (Cùng gọi riêng là "Bên", gọi chung là "Các Bên")
                  </p>
                </div>
              </div>

              {/* Main Content */}
              {
                contracts.filter(c => c.name === (contractInfo?.name || decodedContractInfo?.name)).map((contract, index) => {
                  const MainContent = contract.mainContent as any;
                  return <MainContent key={index} data={contractData} />;
                })
              }

              {/* Signatures */}
              <div className="grid grid-cols-2 !gap-8 !mt-8 !pt-6 !border-t pdf-block">
                <div className="text-center">
                  <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !mb-2">ĐẠI DIỆN BÊN A</p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-4">(Ký tên, đóng dấu)</p>
                  <div className="!h-20 flex items-center justify-center !border-b !border-gray-300 dark:!border-gray-900">
                    <p className="!text-sm !text-gray-400 italic">Đã ký điện tử</p>
                  </div>
                  <p className="!text-sm !font-medium !text-gray-900 dark:!text-white !mt-2">
                    {contractData.company.representative.toUpperCase()}
                  </p>
                </div>

                <div className="text-center">
                  <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !mb-2">BÊN B</p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-4">(Ký, ghi rõ họ tên)</p>
                  <div className="!h-20 flex items-center justify-center">
                    {signature ? (
                      <img src={signature} alt="Signature" className="max-h-full" />
                    ) : (
                      <p className="!text-sm !text-gray-400 italic">Chưa ký</p>
                    )}
                  </div>
                  <p className="!text-sm !font-medium !text-gray-900 dark:!text-white mt-2">
                    {contractData.investor.fullName.toUpperCase()}
                  </p>
                </div>
              </div>

              {/* Integrity Hash */}
              {/* <div className="!mt-12 !pt-4 !border-t !border-gray-100 !text-center pdf-block">
                <p className="!text-[10px] !text-gray-400 !font-mono">
                  Integrity Hash (SHA-256): {contractHash}
                </p>
              </div> */}
            </div>
            {/* Appendix Contract */}
            {
              contracts.filter(c => c.name === (contractInfo?.name || decodedContractInfo?.name) && c.appendixHeader).map((contract, index) => {
                const AppendixHeader = contract.appendixHeader as any;
                const AppendixContent = contract.appendixContent as any;

                // Calculate values based on contract type
                const is13W = contract.name === 'TRADER';
                const price = Number(contractInfo?.price ?? decodedContractInfo?.price) || 0;

                const appendixValues = is13W ? {
                  preTaxValue: (price / 1.1).toLocaleString('vi-VN'),
                  vatAmount: (price - (price / 1.1)).toLocaleString('vi-VN'),
                  totalValue: price.toLocaleString('vi-VN')
                } : {
                  investment: {
                    packageName: contractInfo?.name || decodedContractInfo?.name || '',
                    quantity: '01',
                    investmentAmount: price.toLocaleString('vi-VN')
                  },
                  payment: {
                    deposit: price.toLocaleString('vi-VN'),
                    depositDate: dayjs(contractData.depositDate).format('DD/MM/YYYY'),
                    finalPayment: price.toLocaleString('vi-VN'),
                    finalPaymentDate: dayjs(contractData.finalPaymentDate).format('DD/MM/YYYY')
                  }
                };

                return (
                  <div key={index} className="!bg-white dark:!bg-dark-card !rounded-xl !shadow-sm !p-8 !relative !mt-6" ref={appendixRef}>
                    <AppendixHeader
                      contractNumber={contractData.contractNumber}
                      mainContractNumber={contractData.contractNumber}
                      mainContractDate={contractData.signDate}
                      appendixNumber={`${contractData.contractNumber}/PL`}
                    />

                    <div className="!mb-6 pdf-block">
                      <p className="!text-sm !text-gray-700 dark:!text-gray-300 !mb-4">
                        Hôm nay, ngày {contractData.signDate} tại Công ty Cổ phần BeQ Holdings, chúng tôi gồm có:
                      </p>

                      <div className="!space-y-4">
                        <div>
                          <h4 className="!font-semibold !text-gray-900 dark:!text-white !mb-2">Bên A: {contractData.company.name}</h4>
                          <div className="!text-sm !text-gray-900 dark:!text-gray-400 !space-y-1">
                            <p>Địa chỉ: {contractData.company.address}</p>
                            <p>MST: {contractData.company.taxCode}</p>
                            <p>Số tài khoản: {contractData.company.bankAccount} - {contractData.company.bank}</p>
                            <p>Đại diện: {contractData.company.representative} – {contractData.company.position}</p>
                            <p>(CCCD: {contractData.company.representativeId}, cấp ngày {contractData.company.representativeIssueDate})</p>
                          </div>
                        </div>

                        <div>
                          <h4 className="!font-semibold !text-gray-900 dark:!text-white !mb-2 flex flex-row relative">
                            <div className="inline-block">
                              Bên B: {contractData.investor.fullName || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}
                            </div>
                          </h4>
                          <div className="!text-sm !text-gray-900 dark:!text-gray-400 !space-y-1 w-1/2">
                            <p>CCCD: {contractData.investor.idNumber + ' - Ngày cấp: ' + contractData.investor.issueDate || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                            <p>Ngày sinh: {contractData.investor.dob || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                            <p>Địa chỉ: {contractData.investor.address || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                            <p>Email: {contractData.investor.email || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                            <p>SĐT: {contractData.investor.phone || <span className="inline-block border-b border-gray-900 w-1/2 h-px ml-1 translate-y-[-2px]"></span>}</p>
                            <p>Số tài khoản: {contractData.investor.bankAccount || <span className="inline-block border-b border-gray-900 w-1/4 h-px ml-1 translate-y-[-2px]"></span>} - {contractData.investor.bank || <span className="inline-block border-b border-gray-900 w-1/4 h-px ml-1 translate-y-[-2px]"></span>}</p>
                          </div>
                        </div>

                        <p className="!text-sm !text-gray-900 dark:!text-gray-400 italic">
                          (Cùng gọi riêng là "Bên", gọi chung là "Các Bên")
                        </p>
                      </div>
                    </div>

                    <AppendixContent
                      data={contractData}
                      values={appendixValues}
                      mainContractNumber={contractData.contractNumber}
                      mainContractDate={contractData.signDate}
                    />

                    {/* Signatures */}
                    <div className="grid grid-cols-2 !gap-8 !mt-8 !pt-6 !border-t pdf-block">
                      <div className="text-center">
                        <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !mb-2">ĐẠI DIỆN BÊN A</p>
                        <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-4">(Ký tên, đóng dấu)</p>
                        <div className="!h-20 flex items-center justify-center !border-b !border-gray-300 dark:!border-gray-900">
                          <p className="!text-sm !text-gray-400 italic">Đã ký điện tử</p>
                        </div>
                        <p className="!text-sm !font-medium !text-gray-900 dark:!text-white !mt-2">
                          {contractData.company.representative.toUpperCase()}
                        </p>
                      </div>

                      <div className="text-center">
                        <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !mb-2">BÊN B</p>
                        <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-4">(Ký, ghi rõ họ tên)</p>
                        <div className="!h-20 flex items-center justify-center">
                          {signature ? (
                            <img src={signature} alt="Signature" className="max-h-full" />
                          ) : (
                            <p className="!text-sm !text-gray-400 italic">Chưa ký</p>
                          )}
                        </div>
                        <p className="!text-sm !font-medium !text-gray-900 dark:!text-white mt-2">
                          {contractData.investor.fullName.toUpperCase()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>

          {/* Right and fixed: Signature Panel */}
          <div className="lg:col-span-1">
            <div className="!bg-white dark:!bg-dark-card !rounded-xl !shadow-sm !p-6 !sticky !top-24">
              <div className="flex items-center !gap-3 !mb-2">
                <div>
                  <h3 className="!text-lg !font-semibold !text-gray-900 dark:!text-white !mb-0">
                    Đồng ý ký hợp đồng
                  </h3>
                  <p className="!text-sm !text-gray-700 dark:!text-gray-200">
                    Xác nhận thông tin và ký điện tử
                  </p>
                </div>
              </div>

              <div className="!space-y-4">
                <div>
                  <label className="block !text-sm !font-medium !text-gray-700 dark:!text-gray-300 !mb-2">
                    Chữ ký điện tử <span className="!text-red-500">*</span>
                  </label>
                  <div className='flex !justify-center'>
                    <SignaturePad onSignatureChange={handleSignatureChange} width={400} height={150} />
                  </div>
                  {!signature && (
                    <p className="!text-xs !text-red-500 !mt-1">Vui lòng vẽ chữ ký để tiếp tục</p>
                  )}
                </div>

                {showPreview && signature && (
                  <div className="!bg-gray-50 dark:!bg-gray-800 !rounded-md !p-4">
                    <p className="!text-sm !font-medium !text-gray-700 dark:!text-gray-300 !mb-2">
                      Xem trước chữ ký:
                    </p>
                    <div className="!bg-white dark:!bg-gray-900 !border-2 !border-gray-200 dark:!border-gray-700 !rounded-md !p-3 flex items-center justify-center">
                      <img src={signature} alt="Signature preview" className="!max-h-20" />
                    </div>
                    {/* <div className="flex items-center !gap-2 !mt-2 !text-xs !text-green-600 dark:!text-green-400">
                      <Shield className="!w-4 !h-4" />
                      <span>Được bảo vệ bởi công nghệ mã hóa</span>
                    </div> */}
                  </div>
                )}

                <button
                  onClick={() => exportToPDF(true)}
                  disabled={isExporting}
                  className="!w-full flex items-center justify-center !gap-2 !px-4 !py-2.5 !text-sm !font-medium !text-gray-700 dark:!text-gray-300 !bg-white dark:!bg-gray-800 !border !border-gray-300 dark:!border-gray-900 !rounded-lg hover:!bg-gray-50 dark:hover:!bg-gray-700 disabled:!opacity-50 !transition-colors"
                >
                  <Download className="!w-4 !h-4" />
                  {isExporting ? 'Đang xuất PDF...' : 'Tải hợp đồng PDF'}
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!checkContractReady() || isExporting}
                  className="!w-full !px-6 !py-3 !bg-blue-600 !text-white !rounded-md !font-semibold hover:!bg-blue-700 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors"
                >
                  Ký hợp đồng ngay
                </button>
                {isExporting && (
                  <p className="!text-sm !text-gray-500 dark:!text-gray-400 !mt-2">Đang xử lý, vui lòng chờ...</p>
                )}
                {
                  !checkContractReady() && (
                    <p className="!text-sm !text-red-500 !mt-2">Vui lòng hoàn tất thông tin và chữ ký để ký hợp đồng</p>
                  )
                }

                <div className="!bg-blue-50 dark:!bg-blue-900/20 !border !border-blue-200 dark:!border-blue-800 !rounded-md !p-3 !space-y-1">
                  <p className="!text-xs !text-blue-800 dark:!text-blue-300 flex items-center !gap-2">
                    <Shield className="!w-3 !h-3" />
                    Chữ ký điện tử có giá trị pháp lý
                  </p>
                  <p className="!text-xs !text-blue-800 dark:!text-blue-300 flex items-center !gap-2">
                    <Shield className="!w-3 !h-3" />
                    Hợp đồng được lưu trữ an toàn
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {
        (isExporting || isSubmitting) && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-xl flex flex-col items-center gap-4">
              <Spinner size={40} />
              <p className="text-gray-900 dark:text-white font-medium">
                {isExporting ? 'Đang tạo hợp đồng...' : 'Đang xử lý...'}
              </p>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default SigningContract;
